/**
 * Purchase tax type comes from the receipt amounts, not the category.
 * Category may only force a non-recoverable type via vat_recovery_blocked.
 *
 * Org tax types: INPUT2 (20%), RRINPUT (5%), ZERORATEDINPUT (0%),
 * EXEMPTINPUT (exempt, e.g. insurance), NONE, REVERSECHARGES
 * (imported services with no UK VAT on the invoice).
 */

export const RECEIPT_INPUT_TAX_TYPES = [
  'INPUT2',
  'RRINPUT',
  'ZERORATEDINPUT',
  'EXEMPTINPUT',
  'NONE',
  'REVERSECHARGES',
] as const;
export type ReceiptInputTaxType = (typeof RECEIPT_INPUT_TAX_TYPES)[number];

/** Forced when vat_recovery_blocked — do not reclaim, even if the till shows 20%. */
export const BLOCKED_RECOVERY_TAX_TYPE: ReceiptInputTaxType = 'NONE';

/**
 * B2B imported services — reverse charge when the invoice shows no UK VAT.
 * Parked until Jack puts Terratech’s VAT on Anthropic / Replit / Vercel.
 * Do not infer establishment from currency or a GB VAT prefix.
 * Flag lives on receipt_supplier_categories (true / false / null).
 *
 * Zero VAT + imported-service category + flag null → refuse (TaxDerivationError),
 * surface the reason via receipts_blocked. Never default that case to
 * ZERORATEDINPUT — that understates output tax as well as mis-coding the input.
 * Zero VAT outside those categories → ZERORATEDINPUT, no flag needed.
 * Flag false → ZERORATEDINPUT. Flag true → REVERSECHARGES.
 * Accountant confirm stays on reverse charge. Do not set historic rows.
 */
export const IMPORTED_SERVICE_CATEGORIES = [
  'software',
  'professional',
  'phone_internet',
] as const;

export class TaxDerivationError extends Error {
  readonly code = 'tax_underivable';
  constructor(message: string) {
    super(message);
    this.name = 'TaxDerivationError';
  }
}

export type DerivedReceiptTax = {
  taxType: ReceiptInputTaxType;
  source: 'receipt' | 'vat_recovery_blocked' | 'reverse_charge';
  note: string;
};

function matchesRate(net: number, vat: number, rate: number): boolean {
  return Math.abs(Math.round(net * rate) - vat) <= 1;
}

/**
 * Derive the Xero purchase tax type.
 * Blocked categories always return NONE.
 * Otherwise: insurance at 0% → EXEMPTINPUT, 0% → ZERORATEDINPUT,
 * 20% of net → INPUT2, 5% → RRINPUT.
 * Any other split is refused — never guess INPUT2.
 */
export function deriveReceiptTaxType(opts: {
  net_pence: number | null;
  vat_pence: number | null;
  vatRecoveryBlocked: boolean;
  currency?: string | null;
  category?: string | null;
}): DerivedReceiptTax {
  if (opts.vatRecoveryBlocked) {
    return {
      taxType: BLOCKED_RECOVERY_TAX_TYPE,
      source: 'vat_recovery_blocked',
      note: 'VAT recovery blocked for this category — will post as NONE (not reclaimed). Flagged for your accountant.',
    };
  }
  const net = opts.net_pence;
  const vat = opts.vat_pence;
  if (net == null || vat == null) {
    throw new TaxDerivationError(
      'Need net and VAT to derive tax type — not posted',
    );
  }
  const currency = opts.currency ? opts.currency.toUpperCase() : null;
  const importedServiceCategory = IMPORTED_SERVICE_CATEGORIES.includes(
    (opts.category ?? '') as (typeof IMPORTED_SERVICE_CATEGORIES)[number],
  );
  if (vat === 0) {
    if (opts.category === 'insurance') {
      return {
        taxType: 'EXEMPTINPUT',
        source: 'receipt',
        note: 'VAT is 0.00 on insurance — treated as exempt input (EXEMPTINPUT), matching Xero 433.',
      };
    }
    if (importedServiceCategory && !currency) {
      throw new TaxDerivationError(
        'Need currency to decide reverse charge vs a zero-rated input — never assumed GBP',
      );
    }
    if (importedServiceCategory && currency !== 'GBP') {
      return {
        taxType: 'REVERSECHARGES',
        source: 'reverse_charge',
        note: 'Foreign-currency service with no UK VAT on the invoice — reverse charge (REVERSECHARGES), not a zero-rated input claim. Accountant should confirm.',
      };
    }
    return {
      taxType: 'ZERORATEDINPUT',
      source: 'receipt',
      note: 'VAT is 0.00 — treated as zero-rated input.',
    };
  }
  if (matchesRate(net, vat, 0.2)) {
    return {
      taxType: 'INPUT2',
      source: 'receipt',
      note: 'VAT is 20% of net — standard-rated input.',
    };
  }
  if (matchesRate(net, vat, 0.05)) {
    return {
      taxType: 'RRINPUT',
      source: 'receipt',
      note: 'VAT is 5% of net — reduced-rate input.',
    };
  }
  throw new TaxDerivationError(
    `VAT £${(vat / 100).toFixed(2)} is not 0%, 5% or 20% of net £${(net / 100).toFixed(2)} — not posted, no guess`,
  );
}
